import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { ProviderAppTile } from "./ProviderAppTile";
import { VERIFIED_INSURERS } from "../lib/verifiedInsurers";

// Iter-440 — Desktop hero insurer cluster (xl+). Brand recognition only: no
// links, no quote activity, no persistent selection. Anchored to the left
// hero column in em units of the 96px headline (wrapper is text-8xl, same as
// the h1 at xl), so it tracks "In 2 minutes." rather than the form edge.
// Click/tap = brief micro-feedback that returns to rest (~700ms).
const PICKS = ["geico", "liberty-mutual", "progressive", "travelers", "allstate"];
// Staggered 5-tile cluster: A B / C D / E — the fifth extends downward, not right.
const POS = [
  { x: 0, y: 12 },
  { x: 54, y: 0 },
  { x: 6, y: 60 },
  { x: 60, y: 48 },
  { x: 30, y: 98 },
];
const toTiles = (picks) => picks.map((slug) => VERIFIED_INSURERS.find((i) => i.slug === slug)).filter(Boolean);
const TILES = toTiles(PICKS);
// Iter-465 — Hub variant: USAA replaces Travelers (no Travelers rate data to compare).
export const HUB_PICKS = ["geico", "liberty-mutual", "progressive", "usaa", "allstate"];
const TAP_MS = 700;

// Iter-461 — Shared by the homepage hero and the /auto-insurance hub banner.
// Iter-462 — `selectable` turns tiles into toggle buttons (hub only).
export const ProviderCluster = ({ className, testid = "hero-provider-cluster", tilePrefix = "hero-provider", selectable = false, selected = [], onToggle, canSelect = () => true, stashed = [], picks }) => {
  const TILES_ = picks ? toTiles(picks) : TILES;
  const [tapped, setTapped] = useState(null);
  const timer = useRef(null);
  const tap = (slug) => {
    clearTimeout(timer.current);
    setTapped(slug);
    timer.current = setTimeout(() => setTapped(null), TAP_MS);
  };
  const label = `Insurers in our comparison network include ${TILES_.map((t) => t.name).join(", ")}`;
  return (
    <div
      role={selectable ? "group" : "img"}
      aria-label={selectable ? "Pick two insurers to compare side-by-side" : label}
      data-testid={testid}
      className={`w-[116px] h-[160px] ${className}`}
    >
      {TILES_.map((ins, i) => {
        const style = { left: POS[i].x, top: POS[i].y, zIndex: tapped === ins.slug ? 10 : i + 1, animationDelay: `${0.6 + i * 0.22}s` };
        const tile = <ProviderAppTile insurer={ins} state="color" size="sm" showLabel={false} decorative eager />;
        if (selectable) {
          const on = selected.includes(ins.slug);
          const enabled = canSelect(ins.slug);
          return (
            <button
              key={ins.slug}
              type="button"
              aria-pressed={on}
              aria-label={enabled ? `${on ? "Remove" : "Add"} ${ins.name} ${on ? "from" : "to"} comparison` : `${ins.name} — not in our rate table yet`}
              disabled={!enabled}
              title={enabled ? undefined : `${ins.name} isn't in our rate table yet`}
              data-testid={`${tilePrefix}-${ins.slug}`}
              data-selected={on ? "true" : undefined}
              onClick={() => onToggle(ins.slug)}
              className="hero-provider-chip absolute rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981] disabled:cursor-default"
              style={style}
            >
              {tile}
              {stashed.includes(ins.slug) && (
                <span
                  data-testid={`${tilePrefix}-${ins.slug}-in-compare`}
                  title={`${ins.name} is already in Side-by-Side`}
                  className="absolute -top-1 -right-1 z-10 w-3.5 h-3.5 rounded-full bg-[#10B981] ring-2 ring-[#0A2540] grid place-items-center"
                >
                  <Check className="w-2.5 h-2.5 text-[#0A2540]" strokeWidth={4} />
                </span>
              )}
            </button>
          );
        }
        return (
          <span
            key={ins.slug}
            aria-hidden="true"
            data-testid={`${tilePrefix}-${ins.slug}`}
            data-tapped={tapped === ins.slug ? "true" : undefined}
            onClick={() => tap(ins.slug)}
            className="hero-provider-chip absolute"
            style={style}
          >
            {tile}
          </span>
        );
      })}
    </div>
  );
};

export const HeroProviderCluster = () => (
  <ProviderCluster className="hidden xl:block absolute text-8xl left-[calc(5.7em+14px)] top-[calc(1.96em+2rem+10px)] z-20" />
);

export default HeroProviderCluster;
